/**
 * Turning one `audit_log` row into something a person can read (spec §13, §14.3).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A ROW THIS MODULE DOES NOT RECOGNISE STILL HAS TO LIST, AND THAT IS THE WHOLE
 * SHAPE OF THIS FILE.
 *
 * `audit_log.action` and `entity_type` are `VarChar(64)` rather than enums
 * precisely because the catalogue grows every time the app learns to record
 * something — `packages/types/src/audit.ts` says so where it refuses to make the
 * filter an enum. A six-year table WILL contain actions written by a version of
 * this app that no longer exists, and a window that hid what it could not parse
 * would be a window that quietly stops being evidence. So every lookup here
 * answers `null` rather than throwing or guessing, and the screen falls back to
 * the stored string.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * NOTHING HERE IS JSX, so the catalogues and the payload flattening can be
 * tested as plain TypeScript — the same split `features/notifications/
 * notification-copy.ts` uses.
 */

/* -------------------------------------------------------------------------- */
/* 1. The named views                                                          */
/* -------------------------------------------------------------------------- */

/**
 * One saved question, as a query the API actually accepts.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE OWNER'S QUESTION IS "WHO LOGGED IN WHEN", AND IT HAS TO BE ONE OR TWO TAPS
 * RATHER THAN A SCROLL. `GET /audit` filters on `action` and on `entityType`,
 * each an exact single value — there is no "any of these" — so a view is exactly
 * one of those, never a set the client would have to fetch twice and merge.
 *
 * `people` IS THE ONE-TAP ANSWER, and it works because every auth event writes
 * `entity_type = "user"`: sign-ins, sign-outs, refusals after a correct
 * credential, lockouts, and every attempt against a frozen account, alongside
 * the account changes themselves. The `auth.*` views below narrow that to a
 * single event when the reader wants only one, which is the second tap.
 *
 * ORDERED BY WHO ASKS. The account and sign-in views come first because they are
 * why this screen was asked for; the document registers follow.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface AuditView {
  /** Suffix of the `audit.view.*` label key, and the `?view=` value in the URL. */
  key: string;
  entityType?: string;
  action?: string;
}

export const AUDIT_VIEWS: readonly AuditView[] = Object.freeze([
  { key: "all" },
  { key: "people", entityType: "user" },
  { key: "signInPin", action: "auth.owner_login" },
  { key: "signInPassword", action: "auth.admin_login" },
  { key: "signOut", action: "auth.logout" },
  { key: "wrongPin", action: "auth.owner_login_failed" },
  { key: "wrongPassword", action: "auth.admin_login_failed" },
  { key: "turnedAway", action: "auth.login_refused" },
  { key: "lockedOut", action: "auth.account_locked" },
  { key: "permissionRefused", action: "auth.denied" },
  { key: "roleChanges", action: "user.role_changed" },
  { key: "invoices", entityType: "invoice" },
  { key: "quotations", entityType: "quotation" },
  { key: "challans", entityType: "challan" },
  { key: "creditNotes", entityType: "credit_note" },
  { key: "customers", entityType: "customer" },
  { key: "items", entityType: "item" },
  { key: "payments", entityType: "payment" },
  { key: "company", entityType: "company" },
  { key: "taxCodes", entityType: "hsn_code" },
] as const);

/** The view named in `?view=`, or "everything" for anything unrecognised. */
export function auditViewByKey(key: string | null): AuditView {
  const found = AUDIT_VIEWS.find((view) => view.key === key);
  return found ?? { key: "all" };
}

/* -------------------------------------------------------------------------- */
/* 2. What the action means                                                    */
/* -------------------------------------------------------------------------- */

/**
 * `audit_log.action` → a translation key, for the actions this build knows.
 *
 * THE KEYS ARE THE STORED STRINGS, VERBATIM, so this map can be checked against
 * `grep -o 'action: "[a-z_.]*"'` over `apps/api`. The values are flattened
 * (`auth.owner_login` → `authOwnerLogin`) because i18next reads a dot as a level
 * of nesting, and `audit.action.auth.owner_login` would look for an `owner_login`
 * key inside an `auth` object that is not there.
 *
 * THE `auth.*` AND `user.*` FAMILIES ARE HERE BECAUSE THEIR NAMES DO NOT READ
 * PLAINLY. "auth.login_refused" is the one event on this screen that means
 * something a reader would not guess — the credential was CORRECT and the person
 * was turned away anyway — and getting that wrong is the difference between
 * "she typed her password wrong" and "her role is switched off". The document
 * actions are named `<entity>.<verb>` and read as themselves; they are still
 * listed so the screen says "Cancelled a bill" rather than making a technical
 * reader parse an identifier, but nothing breaks when one is missing.
 */
export const AUDIT_ACTION_KEYS: Readonly<Record<string, string>> = Object.freeze({
  /* Sign-in and sign-out — every role, audited since 2026-08-17. */
  "auth.owner_login": "authOwnerLogin",
  "auth.admin_login": "authAdminLogin",
  "auth.logout": "authLogout",
  "auth.owner_login_failed": "authOwnerLoginFailed",
  "auth.admin_login_failed": "authAdminLoginFailed",
  "auth.login_refused": "authLoginRefused",
  "auth.login_blocked": "authLoginBlocked",
  "auth.account_locked": "authAccountLocked",
  "auth.denied": "authDenied",

  /* Accounts (§2.1's `user.manage`). One action per FACT, not per request. */
  "user.created": "userCreated",
  "user.renamed": "userRenamed",
  "user.role_changed": "userRoleChanged",
  "user.deactivated": "userDeactivated",
  "user.reactivated": "userReactivated",
  "user.credential_reset": "userCredentialReset",
  "user.update": "userUpdate",

  /* Documents. */
  "invoice.create": "invoiceCreate",
  "invoice.update": "invoiceUpdate",
  "invoice.cancel": "invoiceCancel",
  "invoice.record_manual": "invoiceRecordManual",
  "invoice.record_irn": "invoiceRecordIrn",
  "invoice.record_ewb": "invoiceRecordEwb",
  "invoice.clear_ewb": "invoiceClearEwb",
  "quotation.create": "quotationCreate",
  "quotation.update": "quotationUpdate",
  "quotation.status": "quotationStatus",
  "quotation.convert": "quotationConvert",
  "quotation.void": "quotationVoid",
  "challan.create": "challanCreate",
  "challan.update": "challanUpdate",
  "challan.return": "challanReturn",
  "challan.cancel": "challanCancel",
  "challan.record_ewb": "challanRecordEwb",
  "challan.clear_ewb": "challanClearEwb",
  "creditnote.create": "creditnoteCreate",
  "creditnote.cancel": "creditnoteCancel",
  "document_draft.status": "documentDraftStatus",

  /* Everything else that writes a row. */
  "customer.create": "customerCreate",
  "customer.update": "customerUpdate",
  "item.create": "itemCreate",
  "item.create_from_line": "itemCreateFromLine",
  "item.update": "itemUpdate",
  "item.adjust_stock": "itemAdjustStock",
  "payment.create": "paymentCreate",
  "payment.delete": "paymentDelete",
  "message.send": "messageSend",
  "company.update": "companyUpdate",
  "company.counters_override": "companyCountersOverride",
  "company.seed": "companySeed",
  "hsn_code.create": "hsnCodeCreate",
  "hsn_code.rate_change": "hsnCodeRateChange",
  "hsn_code.supersede": "hsnCodeSupersede",
  "hsn_code.verify": "hsnCodeVerify",
});

/**
 * The `audit.action.*` key for a stored action, or `null` when this build has
 * never heard of it.
 *
 * `null` IS A NORMAL ANSWER, not a failure: the row still lists, showing the
 * stored string. See the header.
 */
export function auditActionKey(action: string): string | null {
  const suffix = Object.prototype.hasOwnProperty.call(AUDIT_ACTION_KEYS, action)
    ? AUDIT_ACTION_KEYS[action]
    : undefined;
  return suffix === undefined ? null : `audit.action.${suffix}`;
}

/**
 * `audit_log.entity_type` → an `audit.entity.*` key, or `null`.
 *
 * Kept separate from the action map because the two columns are independent: a
 * row can carry a known entity and an unknown action (a verb added after this
 * bundle shipped), and naming the thing it happened TO is still worth doing.
 */
export const AUDIT_ENTITY_KEYS: Readonly<Record<string, string>> = Object.freeze({
  user: "user",
  invoice: "invoice",
  quotation: "quotation",
  challan: "challan",
  credit_note: "creditNote",
  customer: "customer",
  item: "item",
  payment: "payment",
  message: "message",
  company: "company",
  hsn_code: "hsnCode",
  document_draft: "documentDraft",
  invoice_line: "invoiceLine",
  quotation_line: "quotationLine",
  delivery_challan_line: "challanLine",
  credit_note_line: "creditNoteLine",
});

export function auditEntityKey(entityType: string): string | null {
  const suffix = Object.prototype.hasOwnProperty.call(AUDIT_ENTITY_KEYS, entityType)
    ? AUDIT_ENTITY_KEYS[entityType]
    : undefined;
  return suffix === undefined ? null : `audit.entity.${suffix}`;
}

/* -------------------------------------------------------------------------- */
/* 3. What a role change actually granted                                      */
/* -------------------------------------------------------------------------- */

/**
 * The capabilities a role change handed over and took away.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THIS IS THE SUBSTANCE OF THE ROW, NOT AN EXTRA. A role change is the one write
 * in this system that hands somebody new POWER, and `ROLE_CAPABILITIES` will not
 * be what it is today in six years — so "ACCOUNTANT → ADMIN" alone is a fact the
 * reader cannot evaluate. The server therefore writes `gained` and `lost` onto
 * the row itself (services/users.ts), for the same reason an invoice line
 * snapshots `gst_rate_bp`: the row has to answer for itself, later, without the
 * reader knowing what the map said on the day.
 *
 * Rendering only the two role names would throw that away and leave the screen
 * showing strictly less than the log holds.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Reads defensively — `after` is `unknown`, arriving from a JSON column that has
 * been written by several versions of this app and, during an incident, possibly
 * by a person at a MariaDB prompt.
 */
export interface CapabilityDelta {
  gained: readonly string[];
  lost: readonly string[];
}

export function capabilityDelta(after: unknown): CapabilityDelta | null {
  if (typeof after !== "object" || after === null || Array.isArray(after)) return null;

  const gained = stringList((after as Record<string, unknown>).gained);
  const lost = stringList((after as Record<string, unknown>).lost);
  if (gained === null && lost === null) return null;

  return { gained: gained ?? [], lost: lost ?? [] };
}

function stringList(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((entry): entry is string => typeof entry === "string");
}

/* -------------------------------------------------------------------------- */
/* 4. The stored payload, flattened                                            */
/* -------------------------------------------------------------------------- */

/**
 * The literal the server writes in place of anything sensitive.
 *
 * IT IS SHOWN AS ITSELF AND NEVER TRANSLATED, DECORATED OR REPLACED. It is the
 * value the log actually holds; presenting it as "hidden", "•••" or an empty
 * cell would be this screen claiming to know something about a row it does not.
 * `redactForAudit` runs again on the way out (services/audit-query.ts) — it is
 * idempotent, so a value blanked going in is still exactly this string.
 */
export const AUDIT_REDACTED = "[REDACTED]";

/** One `key: value` line of a before/after payload. */
export interface PayloadField {
  /** Empty when the payload was not an object — a bare string or number. */
  key: string;
  value: string;
}

/**
 * Keys rendered elsewhere on the row, so the details block does not repeat them.
 *
 * Only the capability delta qualifies: `gained` and `lost` are the headline of a
 * `user.role_changed` row. `capabilities` is deliberately NOT here — it is the
 * full snapshot, it is long, and "what did she hold afterwards" is a different
 * question from "what did this change give her".
 */
const RENDERED_ELSEWHERE: readonly string[] = ["gained", "lost"];

export function payloadFields(payload: unknown): PayloadField[] {
  if (payload === null || payload === undefined) return [];

  if (typeof payload !== "object" || Array.isArray(payload)) {
    return [{ key: "", value: formatPayloadValue(payload) }];
  }

  return Object.entries(payload as Record<string, unknown>)
    .filter(([key]) => !RENDERED_ELSEWHERE.includes(key))
    .map(([key, value]) => ({ key, value: formatPayloadValue(value) }));
}

/**
 * A stored value as one line of text.
 *
 * A STRING COMES BACK UNTOUCHED, which is what keeps `"[REDACTED]"` readable as
 * itself. Numbers, booleans and `null` are printed as they are stored rather
 * than prettified — this is an evidence view for a technical reader, and
 * "null" and "0" are different facts that a friendly dash would merge.
 */
export function formatPayloadValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? "[]" : value.map((entry) => formatPayloadValue(entry)).join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      // A cycle cannot come off JSON, but this runs on data from a text column.
      return String(value);
    }
  }
  return String(value);
}
